import { useCallback, useState, useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI'
import ScoreAdjustmentAnalysisTable from './Table/ScoreAdjustmentAnalysis'
import { useQuery } from '@tanstack/react-query'
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent'
import 'jspdf-autotable'
import jsPDF from 'jspdf'
import axios from 'axios'
import { useSelector } from 'react-redux'
import toasters from '../../../../../../../utils/toasters/Toasters'

const formatPayloadDates = (payload) => {
    if (!payload) return payload
    const formattedPayload = { ...payload }
    if (formattedPayload.startDate) {
        const startDate = new Date(formattedPayload.startDate)
        if (!isNaN(startDate)) {
            formattedPayload.startDate = startDate.toISOString().split('T')[0]
        }
    }
    if (formattedPayload.endDate) {
        const endDate = new Date(formattedPayload.endDate)
        if (!isNaN(endDate)) {
            formattedPayload.endDate = endDate.toISOString().split('T')[0]
        }
    }
    return formattedPayload
}

const ScoreAdjustmentAnalysis = () => {
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const { token } = useSelector((state) => state?.auth)

    const [payload, setPayload] = useState(null)

    // Table and sort states
    const storedTableOptions = JSON.parse(
        sessionStorage.getItem('tableOptions')
    ) || { rowPerPages: 10, activePage: 0 }
    const storedSort = JSON.parse(sessionStorage.getItem('sort')) || null
    const [tableOptions, setTableOptions] = useState(storedTableOptions)
    const [sort, setSort] = useState(storedSort)

    useEffect(() => {
        const startDate = searchParams.get('startDate')
        const endDate = searchParams.get('endDate')
        const code = searchParams.get('code')

        if (startDate || endDate) {
            const urlPayload = { startDate, endDate, code }
            setPayload(urlPayload)
            sessionStorage.setItem(
                'scoreAdjustmentPayload',
                JSON.stringify(urlPayload)
            )
            return
        }

        if (location.state) {
            setPayload(location.state)
            sessionStorage.setItem(
                'scoreAdjustmentPayload',
                JSON.stringify(location.state)
            )
            return
        }

        const storedPayload = sessionStorage.getItem('scoreAdjustmentPayload')
        if (storedPayload) {
            setPayload(JSON.parse(storedPayload))
        }
    }, [location.state, searchParams])

    const formattedPayload = formatPayloadDates(payload)

    // Keep tableOptions and sort in sessionStorage
    useEffect(() => {
        sessionStorage.setItem('tableOptions', JSON.stringify(tableOptions))
    }, [tableOptions])

    useEffect(() => {
        sessionStorage.setItem('sort', JSON.stringify(sort))
    }, [sort])

    // Fetch data
    const getScoreAdjustmentAnalysis = useCallback(async () => {
        if (!formattedPayload) return null

        let apiQueryParams = {
            per_page: tableOptions?.rowPerPages,
            page: tableOptions?.activePage + 1,
            export: 'N',
        }

        if (sort?.columnName && sort?.order) {
            apiQueryParams = {
                ...apiQueryParams,
                order: sort.order,
                sort: sort.columnName,
            }
        }

        return fetchIncidentAnalysisData({
            ...formattedPayload,
            ...apiQueryParams,
            export: 'N',
        })
    }, [sort, tableOptions, formattedPayload])

    const { isLoading, isFetching, data, isError, error } = useQuery(
        ['fetch-score-adjustment', tableOptions, sort, formattedPayload],
        getScoreAdjustmentAnalysis,
        {
            enabled: !!formattedPayload,
            retry: (failureCount, error) => {
                const status = error?.response?.status
                return failureCount < 1 && status >= 400 && status < 600
            },
            retryDelay: 1000,
        }
    )

    // Pagination & sort handlers
    const handlePageClick = (event) =>
        setTableOptions((prev) => ({ ...prev, activePage: event.selected }))
    const handleRowsPerPage = (e) =>
        setTableOptions((prev) => ({
            ...prev,
            rowPerPages: e.target.value,
            activePage: 0,
        }))
    const handleColumnSort = (column, sortDirection) =>
        setSort({ columnName: column?.sortField, order: sortDirection })

    // PDF download
    const downloadPDF = async () => {
        if (!formattedPayload) return

        try {
            const payloadForExport = {
                ...formattedPayload,
                export: 'N',
                pagination: 'false',
            }
            if (sort?.columnName && sort?.order) {
                payloadForExport.sort = sort.columnName
                payloadForExport.order = sort.order
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                payloadForExport,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            )

            const fullData = response?.data?.data?.data || []
            if (!fullData.length) return

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
            })
            doc.setFontSize(18)
            doc.text('Score Adjustment Analysis Report', 14, 18)
            doc.setFontSize(12)
            doc.text(
                `Date Range: ${formattedPayload.startDate} - ${formattedPayload.endDate}`,
                14,
                26
            )

            const tableColumn = [
                'Incident ID',
                'Date',
                'User Name',
                'Incident Name',
                'Incident Description',
                'Score Change',
                'Current Score',
                'Current Status',
            ]
            const tableRows = fullData.map((item) => [
                item?.incident_id || '',
                item?.creat_dt || '',
                item?.user_name_tx || '',
                item?.incidentname_tx || '',
                item?.description_tx || '',
                item?.score_change_total ?? '',
                item?.score_nr ?? '',
                item?.ref_status_cd || '',
            ])

            doc.autoTable({
                startY: 34,
                head: [tableColumn],
                body: tableRows,
                margin: { top: 34, left: 10, right: 0 },
                theme: 'grid',
                headStyles: {
                    fillColor: [0, 81, 242],
                    textColor: [255, 255, 255],
                    fontSize: 11,
                    halign: 'center',
                    cellPadding: 4,
                },
                bodyStyles: {
                    fontSize: 9,
                    cellPadding: 3,
                    textColor: [50, 50, 50],
                    valign: 'middle',
                },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                styles: {
                    lineColor: [180, 180, 180],
                    lineWidth: 0.2,
                    overflow: 'linebreak',
                },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 26 },
                    2: { cellWidth: 32 },
                    3: { cellWidth: 42 },
                    4: { cellWidth: 75 },
                    5: { cellWidth: 22, halign: 'center' },
                    6: { cellWidth: 22, halign: 'center' },
                    7: { cellWidth: 25, halign: 'center' },
                },
                pageBreak: 'auto',
            })

            doc.save('score-adjustment-analysis.pdf')
        } catch (err) {
            console.error('PDF generation error:', err)
        }
    }

    // Excel download
    const downloadFile = (data, fileName = 'download') => {
        const csvBlob = new Blob([data], { type: 'text/csv' })
        const csvUrl = URL.createObjectURL(csvBlob)
        const hiddenElement = document.createElement('a')
        hiddenElement.href = csvUrl
        hiddenElement.target = '_blank'
        hiddenElement.download = `${fileName}.csv`
        hiddenElement.click()
    }

    const ExcelScoreAdjustmentAnalysis = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                { ...formattedPayload, export: 'Y', pagination: 'false' },
                {
                    headers: {
                        'Content-type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            )
            downloadFile(response?.data, 'score-adjustment-report')
        } catch (error) {
            toasters({ type: 'error', message: error?.response?.data?.message })
        }
    }

    const pageCount = Math.ceil((data?.Total || 0) / tableOptions.rowPerPages)

    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <ScoreAdjustmentAnalysisTable
                    isLoading={isLoading || isFetching}
                    data={data}
                    dates={formattedPayload}
                    totaldata={data?.Total}
                    tableOptions={tableOptions}
                    handlePageClick={handlePageClick}
                    handleRowsPerPage={handleRowsPerPage}
                    handleColumnSort={handleColumnSort}
                    downloadPDF={downloadPDF}
                    downloadExcel={ExcelScoreAdjustmentAnalysis}
                    pageCount={pageCount}
                    reportData={formattedPayload}
                    reportCode={payload?.code}
                    reportTableData={data?.data}
                    generatePDFFunction={downloadPDF}
                />
            )}
        </>
    )
}

export default ScoreAdjustmentAnalysis
